import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOnline } from '@ndrc/app-sdk/client';
import { useDesk } from '../desk/context';
import { callCore, mutateCore, requestExternalAccess } from '../lib/rpc';
import { submitExternalForm } from '../environments/launch';
import { externalRequestKey } from '../teacher/external-model';
import { t } from '../messages';
import {
  activityEnvironmentReady,
  waitForActivityEnvironment,
  type ActivityEnvironment,
} from './external-launch';

export function ActivityExternalTool({
  tool,
  paused,
}: {
  tool: { templateId: string; label: string; workspaceId: string | null };
  paused: boolean;
}) {
  const { me, observing, openTeacher } = useDesk();
  const online = useOnline();
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const controller = useRef<AbortController | null>(null);
  const keys = useRef(new Map<string, string>());
  useEffect(
    () => () => {
      controller.current?.abort();
    },
    [],
  );
  useEffect(() => {
    if (paused || observing !== null) controller.current?.abort();
  }, [paused, observing]);
  const read = () =>
    callCore<{ environments: ActivityEnvironment[] }>('external.list', {
      workspaceId: tool.workspaceId,
    });
  const sites = useQuery({
    queryKey: ['core', 'external.list', tool.workspaceId],
    queryFn: read,
    enabled: Boolean(tool.workspaceId) && observing === null,
    retry: false,
    refetchInterval: 5000,
  });
  const environment = sites.data?.environments.find(
    (entry) => entry.template_id === tool.templateId && entry.status !== 'archived',
  );
  const unavailable = !online || paused || observing !== null || sites.isError;
  async function open() {
    if (unavailable || busy || controller.current || !environment) return;
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      setError(t('environments.popup'));
      return;
    }
    popup.opener = null;
    popup.document.documentElement.lang = 'fr';
    popup.document.title = tool.label;
    popup.document.body.textContent = t('activity.external.waiting', { name: tool.label });
    const operation = new AbortController();
    controller.current = operation;
    const close = () => popup.close();
    operation.signal.addEventListener('abort', close, { once: true });
    setBusy(true);
    setError('');
    try {
      let target = environment;
      if (!activityEnvironmentReady(target)) {
        const pending = target.jobs?.some(
          (job) => job.status === 'queued' || job.status === 'running',
        );
        if (target.status === 'stopped' && !pending) {
          const scope = `ndrc-cms-start:${me.id}:${target.id}`;
          let key = keys.current.get(scope) ?? crypto.randomUUID();
          try {
            key = externalRequestKey(sessionStorage, scope, () => key);
          } catch {
            /* Session storage is optional. */
          }
          keys.current.set(scope, key);
          await mutateCore(
            'external.command',
            { environmentId: target.id, action: 'start', requestKey: key },
            key,
          );
          keys.current.delete(scope);
          try {
            sessionStorage.removeItem(scope);
          } catch {
            /* Memory fallback remains available. */
          }
        }
        const ready = await waitForActivityEnvironment({
          environmentId: target.id,
          read: async () => (await read()).environments,
          wait: () => new Promise((resolve) => window.setTimeout(resolve, 2000)),
          cancelled: () => operation.signal.aborted || popup.closed,
        });
        if (operation.signal.aborted || popup.closed) return;
        if (!ready) throw new Error(t('activity.external.delayed'));
        target = ready;
      }
      if (operation.signal.aborted || popup.closed) return;
      const form = await requestExternalAccess(target.id);
      if (operation.signal.aborted || popup.closed) return;
      submitExternalForm(popup, form, target.hostname);
    } catch (cause) {
      popup.close();
      if (!operation.signal.aborted)
        setError(cause instanceof Error ? cause.message : t('environments.error'));
    } finally {
      operation.signal.removeEventListener('abort', close);
      controller.current = null;
      setBusy(false);
      if (!operation.signal.aborted) await sites.refetch();
    }
  }
  if (observing !== null) return <p className="activity-locked">{t('environments.observation')}</p>;
  if (sites.isError)
    return (
      <div>
        <p role="alert">{t('environments.loadError')}</p>
        <button
          type="button"
          className="ndrc-button"
          disabled={!online}
          onClick={() => {
            void sites.refetch();
          }}
        >
          {t('environments.refresh')}
        </button>
      </div>
    );
  if (tool.workspaceId && sites.isPending) return <p role="status">{t('environments.loading')}</p>;
  if (!environment)
    return (
      <div>
        <p>{t('activity.external.missing', { name: tool.label })}</p>
        {me.role === 'teacher' && (
          <button type="button" className="ndrc-button" onClick={() => openTeacher('environments')}>
            {t('environments.manage')}
          </button>
        )}
      </div>
    );
  return (
    <div>
      <button
        type="button"
        className="ndrc-button ndrc-button--primary activity-open"
        disabled={unavailable || busy || environment.status === 'failed'}
        onClick={() => {
          void open();
        }}
      >
        {busy
          ? t('activity.external.opening', { name: tool.label })
          : t(activityEnvironmentReady(environment) ? 'activity.open' : 'activity.external.start', {
              name: tool.label,
            })}
      </button>
      {busy && <p role="status">{t('activity.external.waiting', { name: tool.label })}</p>}
      {environment.status === 'failed' && <p role="alert">{t('environments.failed')}</p>}
      {error && (
        <p className="ndrc-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
